import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import validator from 'email-validator';
import SessionStore from 'stores/SessionStore';
import { getDeviceHash } from 'utils/browser';
import { Button } from 'react-bootstrap';
import Alert from 'components/ui/Alert';
import Table from 'components/v2/Table';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import ExternalLink from 'components/v2/ExternalLink';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import FormGroup from 'components/v2/FormGroup';
import DocumentsWebAPIUtils from 'utils/DocumentsWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import Checkbox from 'components/v2/forms/Checkbox';
import Card from 'components/v2/card';
import Pagination from 'components/v2/PaginationV2';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { INVOICE, shipmentDocTypes } from 'config/documents';
import { showToast } from 'stores/ToastStore';

const TOO_MANY_DOCUMENTS = 'TOO_MANY_DOCUMENTS';
const NO_DOCUMENTS_FOUND_ERROR = 'NO_DOCUMENTS_FOUND';
const SHIPMENTS_PER_PAGE = 20;

const types = [...shipmentDocTypes, INVOICE];

export default function ShipmentResults({ referenceNumbers, handleCancel }) {
  const [shipments, setShipments] = useState([]);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [emails, setEmails] = useState();
  const [emailsInvalid, setEmailsInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    setShipments([]);
    setSearching(true);
    setNoResults(false);

    let query = {
      reference: referenceNumbers,
      page,
      per_page: SHIPMENTS_PER_PAGE,
    };

    ShipmentWebAPIUtils.getShipmentsForExportDocs(
      query,
      (res) => {
        const results = res.entity?.items || [];

        setShipments(results);
        setTotalPages(res.entity?.totalPages);
        setTotalCount(res.entity?.totalCount);

        if (results.length === 0) {
          setNoResults(true);
        }

        setSearching(false);
      },
      (res) => {
        console.log('Error', res.body);

        setSearching(false);
        setNoResults(true);
      },
    );
  }, [referenceNumbers, page]);

  const headers = [
    {
      field: 'shipment_id',
      label: t('SHIPMENT_ID'),
    },
    {
      field: 'status',
      label: t('STATUS'),
    },
    {
      field: 'shipper',
      label: t('SHIPPER'),
    },
    { field: 'carrier', label: t('CARRIER') },
  ];

  const columns = [
    {
      field: 'shipment_id',
      render: (shipmentId, row) => {
        return (
          <ExternalLink
            href={`/shipments/${row._id}`}
            title={shipmentId}
            showIcon
          />
        );
      },
    },
    {
      field: 'status',
      render: (status) => {
        return <ShipmentStatusBadge status={status} />;
      },
    },
    {
      field: 'shipper',
      render: (shipper) => {
        return (
          shipper && (
            <ExternalLink
              href={`/users/${shipper._id}`}
              title={shipper.company_name}
              showIcon
            />
          )
        );
      },
    },
    {
      field: 'carrier',
      render: (carrier) => {
        return (
          carrier && (
            <ExternalLink
              href={`/users/${carrier._id}`}
              title={carrier.name}
              showIcon
            />
          )
        );
      },
    },
  ];

  const handleTypeCheck = (type, value) => {
    let updatedTypes = [...selectedTypes];

    if (value) {
      updatedTypes.push(type);
    } else {
      updatedTypes.splice(selectedTypes.indexOf(type), 1);
    }

    setSelectedTypes(Array.from(new Set(updatedTypes)));
  };

  const handleEmailsChange = (value) => {
    const emails = value.replace(/' '/g, '').split(',');
    const invalidEmailAddresses = emails.filter(
      (ea) => !validator.validate(ea),
    );
    if (invalidEmailAddresses?.length > 0) {
      setEmailsInvalid(true);
    } else {
      setEmailsInvalid(false);
    }
    setEmails(value);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);

      const typesToSend = selectedTypes.length
        ? selectedTypes
        : types.map((type) => type.value);

      const deviceHash = getDeviceHash();
      const languageCodeId =
        SessionStore.getLanguageCodeIdPreference(deviceHash) || 'en-us';

      await DocumentsWebAPIUtils.bulkDownloadByReferenceNumbers(
        referenceNumbers,
        typesToSend,
        emails,
        languageCodeId,
      );

      showToast(t('DOCUMENTS_SENT_VIA_EMAIL'), null, 'success');
    } catch (e) {
      if (e.entity?.error === NO_DOCUMENTS_FOUND_ERROR) {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('NO_DOCUMENTS_ARE_FOUND_FOR_DOWNLOAD'),
          'error',
        );
      } else if (e.entity?.error === TOO_MANY_DOCUMENTS) {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('TOO_MANY_DOCUMENTS_PLEASE_FORMULATE_A_MORE_PRECISE_REQUEST'),
          'error',
        );
      } else {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          'error',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h3 className="mb-4">{t('SELECTED_SHIPMENTS')}</h3>
      <div className="overflow-x-auto table-wrapper max-w-xl">
        <Table
          headers={headers}
          columns={columns}
          data={shipments}
          idKey="_id"
        />
        {searching && (
          <div
            className="mt-4 pb-2 flex flex-col items-center"
            data-testid="searchingMessage"
          >
            <LoadingCircle color="white" text={t('LOADING')} />
          </div>
        )}
        {noResults && (
          <div className="mt-4 pb-2 text-center" data-testid="noResultsMessage">
            {t('NO_RESULTS_TO_DISPLAY')}
          </div>
        )}
        {totalPages > 1 && (
          <Pagination
            pageNeighbours={1}
            total={totalCount}
            showCount
            page={page}
            pages={totalPages}
            perPage={SHIPMENTS_PER_PAGE}
            onPageClick={setPage}
          />
        )}
      </div>
      <Card className="mt-4 max-w-xl">
        <div data-testid="downloadForm">
          <FormGroup label={t('SELECT_DOCUMENT_TYPES')} className="mb-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
              {types.map((docType) => (
                <Checkbox
                  key={docType.value}
                  id={docType.value}
                  name={docType.value}
                  value={docType.value}
                  checked={selectedTypes.includes(docType.value)}
                  onChange={(checked) =>
                    handleTypeCheck(docType.value, checked)
                  }
                  disabled={loading}
                >
                  {docType.name}
                </Checkbox>
              ))}
            </div>
          </FormGroup>

          <Label className="mt-4 mb-1">{t('EMAIL_NOTIFICATIONS')}</Label>

          <div className="mt-4 sm:w-80 w-full">
            <Label htmlFor="emails" className="">
              {t('EMAILS')}
            </Label>
            {emailsInvalid && (
              <Alert type="warning">{t('VALID_EMAIL_ONLY')}</Alert>
            )}
            <TextField
              type="text"
              name="emails"
              id="emails"
              value={emails || ''}
              placeholder={t('ENTER_EMAILS_SEPARATED_BY_COMMAS')}
              disabled={loading}
              onChange={handleEmailsChange}
            />
          </div>
          <div className="sm:flex sm:justify-end sm:space-x-4 mt-8">
            {loading && (
              <div data-testid="loadingMessage">
                <LoadingCircle color="white" text={t('LOADING')} />
              </div>
            )}
            <Button
              data-testid="downloadButton"
              bsStyle="green"
              onClick={handleDownload}
              disabled={
                noResults || searching || loading || !emails || emailsInvalid
              }
            >
              {t('EMAIL_DOCUMENTS')}
            </Button>
            <Button
              data-testid="cancelButton"
              className="mt-4 sm:mt-0"
              onClick={handleCancel}
              disabled={loading}
            >
              {t('CANCEL')}
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}

ShipmentResults.propTypes = {
  referenceNumbers: PropTypes.arrayOf(PropTypes.string),
  handleCancel: PropTypes.func,
};
